// Copyright (c) 2021 WiseTime. All rights reserved.

import { forkJoin, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, concatAll, filter, map, mergeMap } from "rxjs/operators"
import * as TimelineActions from "../actions"
import * as Slice from "../slices/stats"
import {
  ListDaySummariesRequestSchema,
  ListDaySummariesResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { t } from "@lingui/macro"
import { splitDateRange } from "../dateTime"
import { showSnackBar } from "../../snackbar/actions"
import { executor, Init, timelineService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"

type RequestWithRange = {
  request: Init<typeof ListDaySummariesRequestSchema>,
  range: { startDateLocal: number, endDateLocal: number },
}

const splitIntoMultipleRequests = (action: { payload: Init<typeof ListDaySummariesRequestSchema> }): RequestWithRange[] => {
  return splitDateRange(Number(action.payload.startDate), Number(action.payload.endDate), 90)
    .map(dateRange => ({
      request: {
        startDate: dateRange.startDateLocal.toString(),
        endDate: dateRange.endDateLocal.toString(),
      },
      range: dateRange,
    }))
}

type ListDaySummariesRpc = (request: Init<typeof ListDaySummariesRequestSchema>) => Observable<ListDaySummariesResponse>

export const listDaySummaries = (listDaySummariesRpc: ListDaySummariesRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.listDaySummaries.match),
      map(splitIntoMultipleRequests),
      mergeMap(requests => forkJoin(
        requests.map(({ request, range }) => listDaySummariesRpc(request)
          .pipe(
            map(response => ({ response, range })),
            map(Slice.setDaySummaries),
          ))
      ).pipe(
        concatAll(),
        catchError(_ => of(showSnackBar({
          message: t({
            id: "msg_get_day_summaries_failed",
            message: "Failed to get day summaries. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
          }),
          messageType: "ERROR",
        }))))
      )
    )

const listDaySummariesRpc = (request: Init<typeof ListDaySummariesRequestSchema>): Observable<ListDaySummariesResponse> =>
  executor(() => timelineService.listDaySummaries(create(ListDaySummariesRequestSchema, request)))

export default listDaySummaries(listDaySummariesRpc)
